import { CLASS_LABEL } from './terms';

export type HeroType = 'melee' | 'ranged' | 'spell';

/** A bonus a hero unlocks automatically on reaching `level`. */
export type HeroSkillEffect =
  | { kind: 'self'; mult: number }        // this hero's DPS
  | { kind: 'all'; mult: number }         // all damage
  | { kind: 'type'; mult: number }        // every hero of this hero's type
  | { kind: 'tap'; mult: number }         // tap damage
  | { kind: 'gold'; mult: number }        // gold from kills
  | { kind: 'critDamage'; mult: number }
  | { kind: 'bossDamage'; mult: number };

export interface HeroSkill {
  level: number;
  effect: HeroSkillEffect;
}

export interface HeroDef {
  id: string;
  name: string;
  type: HeroType;
  color: string;
  baseCost: number;
  baseDps: number;
  skills: readonly HeroSkill[];
}

// ---- Roster ----------------------------------------------------------------------
// The first 10 are hand-tuned for the opening; 11+ follow a fixed spacing: each costs 400x
// the previous (~33 stages later) for 60x the DPS. That makes hero DPS scale ~gold^0.68 across
// the roster. Keep dpsRatio well under costRatio or heroes alone outrun monster HP (1000x did).

interface RosterEntry { id: string; name: string; type: HeroType; color: string; baseCost?: number; baseDps?: number }

const ROSTER: readonly RosterEntry[] = [
  { id: 'knight',   name: 'Block Knight',     type: 'melee',  color: '#e0a44c', baseCost: 50,    baseDps: 4 },
  { id: 'archer',   name: 'Sarge',   type: 'ranged', color: '#6fcf6a', baseCost: 250,   baseDps: 16 },
  { id: 'mage',     name: 'Rag Doll Mage',   type: 'spell',  color: '#e8564f', baseCost: 1200,  baseDps: 62 },
  { id: 'rogue',    name: 'Wind-Up Bot',       type: 'melee',  color: '#9b7be8', baseCost: 6e3,   baseDps: 250 },
  { id: 'cleric',   name: 'Nurse Bunny',    type: 'spell',  color: '#f3e27a', baseCost: 3.2e4, baseDps: 1050 },
  { id: 'paladin',  name: 'Rex',      type: 'melee',  color: '#7fc4f0', baseCost: 2e5,   baseDps: 5200 },
  { id: 'ranger',   name: 'Bella Bow',         type: 'ranged', color: '#48b58c', baseCost: 1.5e6, baseDps: 30000 },
  { id: 'warlock',  name: 'Jester Jinx',         type: 'spell',  color: '#c05ac9', baseCost: 1.4e7, baseDps: 2.2e5 },
  { id: 'dragoon',  name: 'Rodeo Ralph',     type: 'melee',  color: '#f07f3c', baseCost: 1.6e8, baseDps: 1.9e6 },
  { id: 'titan',    name: 'Dart Dan',      type: 'ranged', color: '#ffffff', baseCost: 2.5e9, baseDps: 2.3e7 },
  { id: 'frostmage', name: 'Snow Globe Sage',   type: 'spell',  color: '#9fd8ff' },
  { id: 'berserker', name: 'Hammer Tom',  type: 'melee',  color: '#c9543d' },
  { id: 'gunner',   name: 'Squirt', type: 'ranged', color: '#b08a5a' },
  { id: 'druid',    name: 'Gnome Gus', type: 'spell',  color: '#6fbf4a' },
  { id: 'monk',     name: 'Karate Kitty',      type: 'melee',  color: '#f2b84b' },
  { id: 'hunter',   name: 'Slingshot Sam', type: 'ranged', color: '#8c6d46' },
  { id: 'necro',    name: 'Puppeteer Posy',   type: 'spell',  color: '#7a5c9e' },
  { id: 'samurai',  name: 'Kokeshi Ken',  type: 'melee',  color: '#d94f6a' },
  { id: 'sniper',   name: 'Periscope Pim',    type: 'ranged', color: '#5a7a8c' },
  { id: 'storm',    name: 'Kite Queen',         type: 'spell',  color: '#7ec8e3' },
  { id: 'templar',  name: 'Tin Soldier Tomas',    type: 'melee',  color: '#e8dcb0' },
  { id: 'corsair',  name: 'Captain Cork',    type: 'ranged', color: '#3f6fb5' },
  { id: 'pyro',     name: 'Candle Cleo',    type: 'spell',  color: '#ff8a3d' },
  { id: 'golem',    name: 'Stack',     type: 'melee',  color: '#8f8f8f' },
  { id: 'windrider', name: 'Paper Pilot',     type: 'ranged', color: '#b5f0d8' },
  { id: 'oracle',   name: 'Fortune Cat', type: 'spell', color: '#d6b8ff' },
  { id: 'warlord',  name: 'General Nutcracker', type: 'melee', color: '#a33b2e' },
  { id: 'starbow',  name: 'Astro Ace',       type: 'ranged', color: '#ffe680' },
  { id: 'lich',     name: 'Ghostie', type: 'spell', color: '#5ed1b8' },
  { id: 'phoenix',  name: 'Fire Chief Fred',  type: 'melee',  color: '#ff5e3a' },
  { id: 'voidhunter', name: 'Shadow Ninja', type: 'ranged', color: '#6b4bd6' },
  { id: 'archmage', name: 'Wizard Wick',   type: 'spell',  color: '#4f7bff' },
  { id: 'dragonlord', name: 'Dragon Rider Dara', type: 'melee', color: '#e03c3c' },
  { id: 'eclipse',  name: 'Yo-Yo Twins',   type: 'ranged', color: '#2b2b44' },
  { id: 'eternal',  name: 'Music Box Queen',     type: 'spell',  color: '#fff2c7' },
  // Late roster (36-50): extends new-hero unlocks to ~stage 1,450 so the late game keeps growing.
  { id: 'colossus',    name: 'Big Brick Bertie',       type: 'melee',  color: '#f2a93b' },
  { id: 'railgun',     name: 'Marble Mortar',   type: 'ranged', color: '#ff6fa8' },
  { id: 'nanite',      name: 'Bubble Wizard',          type: 'spell',  color: '#9ad0c2' },
  { id: 'ironclad',    name: 'Sir Rocking Horse',       type: 'melee',  color: '#6c7a89' },
  { id: 'artillery',   name: 'Catapult Carl',     type: 'ranged', color: '#8a9a5b' },
  { id: 'qubit',       name: 'Kaleido',          type: 'spell',  color: '#b19cff' },
  { id: 'aegis',       name: 'Shieldbear',          type: 'melee',  color: '#7fd1ff' },
  { id: 'skyfall',     name: 'Blimp Baron',        type: 'ranged', color: '#ffb26b' },
  { id: 'horizon',     name: 'Starlight',  type: 'spell',  color: '#3d2b6b' },
  { id: 'juggernaut',  name: 'Monster Truck Max',     type: 'melee',  color: '#e0c341' },
  { id: 'photon',      name: 'Laser Tag Lila',         type: 'ranged', color: '#fffbe0' },
  { id: 'chronos',     name: 'Cuckoo',        type: 'spell',  color: '#c9a45c' },
  { id: 'atlas',       name: 'Globe Guardian',          type: 'melee',  color: '#4fa3a5' },
  { id: 'supernova',   name: 'Firework Finn',      type: 'ranged', color: '#ff7847' },
  { id: 'omega',       name: 'The Forgotten Prince',          type: 'spell',  color: '#f5f5ff' },
];

/** Spacing for generated heroes (11+). */
export const HERO_SPACING = { costRatio: 400, dpsRatio: 60 } as const;

/** Special bonus at level 400 rotates through these so every hero feels a bit different. */
const SPECIALS: readonly HeroSkillEffect[] = [
  { kind: 'tap', mult: 1.5 },
  { kind: 'gold', mult: 1.25 },
  { kind: 'critDamage', mult: 1.5 },
  { kind: 'bossDamage', mult: 1.3 },
];

function skillsFor(index: number): HeroSkill[] {
  return [
    { level: 10, effect: { kind: 'self', mult: 2 } },
    { level: 50, effect: { kind: 'all', mult: 1.1 } },
    { level: 100, effect: { kind: 'type', mult: 1.25 } },
    { level: 200, effect: { kind: 'self', mult: 3 } },
    { level: 400, effect: SPECIALS[index % SPECIALS.length]! },
    { level: 800, effect: { kind: 'all', mult: 1.25 } },
    { level: 1600, effect: { kind: 'self', mult: 5 } },
  ];
}

function buildRoster(): HeroDef[] {
  const out: HeroDef[] = [];
  ROSTER.forEach((r, i) => {
    const prev = out[i - 1];
    const baseCost = r.baseCost ?? prev!.baseCost * HERO_SPACING.costRatio;
    const baseDps = r.baseDps ?? prev!.baseDps * HERO_SPACING.dpsRatio;
    out.push({ id: r.id, name: r.name, type: r.type, color: r.color, baseCost, baseDps, skills: skillsFor(i) });
  });
  return out;
}

export const HEROES: readonly HeroDef[] = buildRoster();
export const HERO_BY_ID: ReadonlyMap<string, HeroDef> = new Map(HEROES.map((h) => [h.id, h]));

const TYPE_LABEL: Record<HeroType, string> = CLASS_LABEL;

export function describeHeroSkill(hero: HeroDef, e: HeroSkillEffect): string {
  const x = (m: number) => `x${m}`;
  const pct = (m: number) => `+${Math.round((m - 1) * 100)}%`;
  switch (e.kind) {
    case 'self': return `Damage ${x(e.mult)}`;
    case 'all': return `All damage ${pct(e.mult)}`;
    case 'type': return `All ${TYPE_LABEL[hero.type]} Guardians ${pct(e.mult)}`;
    case 'tap': return `Tap damage ${pct(e.mult)}`;
    case 'gold': return `Coins ${pct(e.mult)}`;
    case 'critDamage': return `Critical damage ${pct(e.mult)}`;
    case 'bossDamage': return `Giant damage ${pct(e.mult)}`;
  }
}
